import type { StationComparisonRow } from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  EmptyState,
  Label,
  MoneyText,
  PageHeader,
  QuantityText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChartWithTable } from "../../../features/fuel/shared.js";
import { useStationComparison } from "../../../features/reports/queries.js";
import {
  comparisonChartOption,
  formatCompactMoney,
  formatPercentValue,
  rangeError,
  rangeLimitFor,
} from "../../../features/reports/report-values.js";
import {
  ReportFilterBar,
  ReportsGate,
  reportSearchShape,
  SectionCard,
  StatTile,
  TableScroll,
  useReportFilter,
} from "../../../features/reports/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";

const searchSchema = z.object(reportSearchShape);

export const Route = createFileRoute("/_app/raporlar/karsilastirma")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ComparisonRoute,
});

const SORTS = [
  "revenue",
  "fuelLitres",
  "marketConversionRate",
  "lossRate",
  "shiftShortage",
  "evUtilization",
] as const;
type SortKey = (typeof SORTS)[number];

/**
 * İstasyon karşılaştırma (Prompt 22, ADR 0022 §8): dönem × istasyon — ciro (KDV dahil / hariç), pompa litresi ve işlemi,
 * market dönüşümü (100 pompa işlemine market fişi), yıkama ve şarj kullanımı, vardiya açıkları, onaylı fire ve fire oranı;
 * toplam satırı. Göstergeler okuma modellerinden; izinsiz sütun "—".
 */
function ComparisonRoute(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("menu.reportsComparison"));
  return (
    <>
      <PageHeader title={t("menu.reportsComparison")} description={t("reports.comparison.description")} />
      <ReportsGate>
        <Comparison />
      </ReportsGate>
    </>
  );
}

function sortValue(row: StationComparisonRow, key: SortKey): Decimal {
  const value = row[key];
  return value === null || value === undefined ? Decimal.from("-1e30") : Decimal.from(String(value));
}

function Comparison(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [state, setState, explicit] = useReportFilter(search, "LAST_30_DAYS");
  const [sort, setSort] = useState<SortKey>("revenue");
  const sortId = useId();
  const maxDays = rangeLimitFor("STATION_COMPARISON");
  const valid = rangeError(state.range, maxDays) === null;
  const comparison = useStationComparison(state.range, state, valid);
  const data = comparison.data;
  const rows = [...(data?.rows ?? [])].sort((left, right) =>
    sortValue(right, sort).cmp(sortValue(left, sort)),
  );

  return (
    <div className="grid gap-6">
      <ReportFilterBar
        reportKey="STATION_COMPARISON"
        state={state}
        onChange={setState}
        maxDays={maxDays}
        explicit={explicit}
      />
      {comparison.error ? <Alert tone="danger">{errorMessage(comparison.error)}</Alert> : null}
      {!data ? (
        comparison.isPending && valid ? (
          <SkeletonRows rows={6} />
        ) : null
      ) : data.rows.length === 0 ? (
        <EmptyState
          title={t("reports.comparison.emptyTitle")}
          description={t("reports.comparison.emptyDescription")}
        />
      ) : (
        <>
          <section
            aria-label={t("reports.comparison.totals")}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            <StatTile
              label={t("reports.metrics.revenue")}
              value={formatCompactMoney(data.total.revenue)}
              hint={<MoneyText value={data.total.revenue} />}
            />
            <StatTile
              label={t("reports.metrics.fuelLitres")}
              value={<QuantityText value={data.total.fuelLitres} unit="LITRE" />}
              hint={t("reports.metrics.transactions", { count: data.total.fuelTransactions ?? 0 })}
            />
            <StatTile
              label={t("reports.metrics.marketConversion")}
              value={formatPercentValue(data.total.marketConversionRate)}
              hint={t("reports.metrics.marketConversionHint")}
            />
            <StatTile
              label={t("reports.metrics.lossRate")}
              value={formatPercentValue(data.total.lossRate)}
              hint={<QuantityText value={data.total.fuelLossLitres} unit="LITRE" />}
            />
            <StatTile
              label={t("reports.metrics.shiftShortage")}
              value={<MoneyText value={data.total.shiftShortage} />}
              hint={t("reports.metrics.outOfTolerance", { count: data.total.shiftOutOfTolerance ?? 0 })}
            />
          </section>

          <SectionCard
            title={t("reports.comparison.chartTitle")}
            description={t("reports.comparison.chartDescription")}
          >
            <ChartWithTable
              ariaLabel={t("reports.comparison.chartAria")}
              option={comparisonChartOption(data.rows, t("reports.metrics.revenue"))}
              height={Math.max(220, data.rows.length * 36 + 60)}
              tableLabel={t("reports.comparison.chartTable")}
            >
              <table className="w-full text-sm">
                <caption className="sr-only">{t("reports.comparison.chartTable")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t("reports.columns.station")}</Th>
                    <Th align="right">{t("reports.metrics.revenue")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.stationId} className="border-t">
                      <td className="px-3 py-2">
                        {row.stationCode} · {row.stationName}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <MoneyText value={row.revenue} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ChartWithTable>
          </SectionCard>

          <SectionCard
            title={t("reports.comparison.tableTitle")}
            description={
              data.refreshedAt
                ? t("reports.comparison.refreshedAt", { at: formatDateTime(data.refreshedAt) })
                : t("reports.comparison.notRefreshed")
            }
            actions={
              <div className="flex items-center gap-2">
                <Label htmlFor={sortId}>{t("reports.comparison.sortBy")}</Label>
                <Select
                  id={sortId}
                  className="w-52"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                >
                  {SORTS.map((key) => (
                    <option key={key} value={key}>
                      {t(`reports.comparison.sorts.${key}`)}
                    </option>
                  ))}
                </Select>
              </div>
            }
          >
            <TableScroll label={t("reports.comparison.tableTitle")}>
              <table className="w-full min-w-[1280px] text-sm">
                <caption className="sr-only">{t("reports.comparison.tableTitle")}</caption>
                <thead className="bg-muted/50">
                  <tr>
                    <Th>{t("reports.columns.station")}</Th>
                    <Th align="right">{t("reports.metrics.revenue")}</Th>
                    <Th align="right">{t("reports.metrics.revenueNet")}</Th>
                    <Th align="right">{t("reports.metrics.fuelLitres")}</Th>
                    <Th align="right">{t("reports.columns.fuelTransactions")}</Th>
                    <Th align="right">{t("reports.columns.marketReceipts")}</Th>
                    <Th align="right">{t("reports.metrics.marketConversion")}</Th>
                    <Th align="right">{t("reports.columns.washPerUnit")}</Th>
                    <Th align="right">{t("reports.columns.evUtilization")}</Th>
                    <Th align="right">{t("reports.metrics.shiftShortage")}</Th>
                    <Th align="right">{t("reports.columns.loss")}</Th>
                    <Th align="right">{t("reports.metrics.lossRate")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <ComparisonTableRow key={row.stationId} row={row} />
                  ))}
                  <ComparisonTableRow row={data.total} total />
                </tbody>
              </table>
            </TableScroll>
            <p className="text-xs text-muted-foreground">{t("reports.comparison.note")}</p>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }): ReactElement {
  return (
    <th scope="col" className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function ComparisonTableRow({
  row,
  total = false,
}: {
  row: StationComparisonRow;
  total?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const cell = "px-3 py-2 text-right tabular";
  return (
    <tr className={total ? "border-t-2 bg-muted/40 font-semibold" : "border-t"}>
      <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-medium">
        {total ? t("reports.columns.total") : `${row.stationCode} · ${row.stationName}`}
      </th>
      <td className={cell}>
        <MoneyText value={row.revenue} />
      </td>
      <td className={cell}>
        <MoneyText value={row.revenueNet} />
      </td>
      <td className={cell}>
        <QuantityText value={row.fuelLitres} unit="LITRE" />
      </td>
      <td className={cell}>{row.fuelTransactions === null ? "—" : formatCount(row.fuelTransactions)}</td>
      <td className={cell}>{row.marketReceipts === null ? "—" : formatCount(row.marketReceipts)}</td>
      <td className={cell}>{formatPercentValue(row.marketConversionRate)}</td>
      <td className={cell}>
        {row.washCyclesPerUnitDay === null ? "—" : Decimal.from(row.washCyclesPerUnitDay).formatTr(2)}
      </td>
      <td className={cell}>{formatPercentValue(row.evUtilization)}</td>
      <td className={cell}>
        <MoneyText value={row.shiftShortage} />
      </td>
      <td className={cell}>
        <QuantityText value={row.fuelLossLitres} unit="LITRE" />
      </td>
      <td className={cell}>{formatPercentValue(row.lossRate)}</td>
    </tr>
  );
}
